/** Explicit browsing intent is transient navigation state. These controls never
 * change adaptation consent, follows, enrolment or private lesson progress. */
export function createLearning(ctx) {
  let disposeBrowse;
  const disposeLearning = () => {
    disposeBrowse?.();
    disposeBrowse = undefined;
  };
  function renderContentScope(target, { destination = false } = {}) {
    const learning = ctx.contentScope === 'learning';
    const choices = ctx.e('nav', 'content-intent');
    choices.setAttribute('aria-label', ctx.uiText('Content to explore'));
    for (const [value, label, route] of [
      ['ordinary', 'Everyday', 'feed'],
      ['learning', 'Learning', 'learning'],
    ]) {
      const selected = learning === (value === 'learning');
      const choice = destination
        ? ctx.link(label, `#/${route}`, 'content-intent-choice')
        : ctx.button(
            label,
            () => {
              if (ctx.contentScope === value) return;
              ctx.contentScope = value;
              ctx.feed.reset(ctx.mode);
              ctx.feedScroll = 0;
              ctx.render();
            },
            'content-intent-choice',
          );
      if (destination && selected) choice.setAttribute('aria-current', 'page');
      if (!destination) choice.setAttribute('aria-pressed', String(selected));
      choice.prepend(ctx.icon(value === 'learning' ? 'compass' : 'house'));
      choices.append(choice);
    }
    target.append(choices);
    return choices;
  }

  function renderLearningHeader(target) {
    disposeLearning();
    const choices = renderContentScope(target, { destination: true });
    if (ctx.contentScope !== 'learning') return;
    const disclosure = ctx.e('details', 'learning-browse');
    const trigger = ctx.e('summary', 'content-intent-choice', ctx.uiText('Browse'));
    trigger.append(ctx.icon('chevron-down'));
    const links = ctx.e('nav', 'learning-browse-links');
    links.id = 'learning-browse-links';
    links.setAttribute('aria-label', ctx.uiText('Learning resources'));
    trigger.setAttribute('aria-controls', links.id);
    trigger.setAttribute('aria-expanded', 'false');
    links.append(
      ctx.link('Browse learning guides', '#/courses', 'text-button'),
      ctx.link('Search learning', '#/search', 'text-button'),
      ctx.link('Explore learning images', '#/explore', 'text-button'),
    );
    const close = (focus = false) => {
      disclosure.open = false;
      trigger.setAttribute('aria-expanded', 'false');
      if (focus) trigger.focus();
    };
    disclosure.addEventListener('toggle', () =>
      trigger.setAttribute('aria-expanded', String(disclosure.open)),
    );
    disclosure.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    });
    const doc = target.ownerDocument ?? globalThis.document;
    const outside = (event) => {
      if (disclosure.open && !disclosure.contains(event.target)) close();
    };
    doc?.addEventListener('pointerdown', outside);
    disposeBrowse = () => {
      close();
      doc?.removeEventListener('pointerdown', outside);
    };
    disclosure.append(trigger, links);
    choices.append(disclosure);
  }
  function renderLearningContext() {
    const card = ctx.e('section', 'insight-card');
    card.append(
      ctx.e('div', 'eyebrow', ctx.uiText('A SUBJECT AT A TIME')),
      ctx.e('h2', '', ctx.uiText('Start with curiosity.')),
      ctx.e(
        'p',
        '',
        ctx.uiText(
          'Browse a reading or follow a guide in order. Each guide explains its published coverage.',
        ),
      ),
      ctx.link('Browse learning guides', '#/courses', 'secondary-button'),
      ctx.link('Back to everyday moments', '#/feed', 'text-button'),
    );
    ctx.context.append(card);
  }
  function renderCourses() {
    renderContentScope(ctx.view, { destination: true });
    ctx.view.append(
      ctx.heading('Learning guides', 'Choose a subject and follow its published reading order.'),
      ctx.link('← Back to Learning', '#/learning', 'text-button'),
    );
    const courses = new Map();
    for (const user of ctx.state.users) {
      for (const course of user.courses ?? []) {
        if (!course.previewOnly && course.publishedLessons > 0)
          courses.set(course.id, { ...course, publisher: user });
      }
    }
    const grid = ctx.e('div', 'learning-guide-grid');
    for (const course of [...courses.values()].sort((a, b) => a.title.localeCompare(b.title))) {
      const card = ctx.e('article', 'learning-guide-card');
      card.append(
        ctx.icon('compass'),
        ctx.append(ctx.e('h2'), ctx.link(course.title, `#/course/${course.id}`)),
        ctx.e(
          'p',
          'subtle',
          `${course.publisher.name} · ${course.publishedLessons} published readings`,
        ),
        ctx.link('Open guide', `#/course/${course.id}`, 'secondary-button'),
      );
      grid.append(card);
    }
    ctx.view.append(
      grid.children.length ? grid : ctx.empty('No published learning guides are available yet.'),
    );
    ctx.view.append(
      ctx.e(
        'p',
        'subtle',
        'Guides show their published coverage. Opening a guide does not enrol you or mark lessons as read.',
      ),
    );
  }
  return {
    renderContentScope,
    renderLearningHeader,
    renderLearningContext,
    renderCourses,
    disposeLearning,
  };
}

/** Present the end of the current authorized reading order without changing it. */
export function feedBoundaryMarker(ctx) {
  const learning = ctx.contentScope === 'learning';
  const text = ctx.feed.meta?.hasEarlier
    ? learning
      ? 'Below are readings you’ve viewed before.'
      : 'Below are moments you’ve viewed before.'
    : learning
      ? 'You’ve reached the end of these learning readings.'
      : 'You’ve reached the end of this feed. Come back for new moments.';
  return ctx.append(
    ctx.e('div', 'caught-up-marker'),
    ctx.icon('circle-check'),
    ctx.append(ctx.e('div'), ctx.e('strong', '', 'You’re all caught up'), ctx.e('p', '', text)),
  );
}

/** An announced loading state names the currently selected collection. */
export function feedLoading(ctx) {
  const status = ctx.e(
    'p',
    'feed-loading',
    ctx.uiText(
      ctx.contentScope === 'learning' ? 'Loading learning readings…' : 'Loading your moments…',
    ),
  );
  status.setAttribute('role', 'status');
  return status;
}
